// ─── Firestore Collection Names ───────────────────────────────────────────────
export const COLLECTIONS = {
  users:               'users',
  seasons:             'seasons',
  userPermissions:     'user_permissions',
  accountDeletions:    'account_deletion_requests',
  topNotification:     'season_top_notification',
  leaderboardSetting:  'season_leaderbaord_setting', // typo kept to match existing DB
  adsController:       'season_ads_controller',
  userNotifications:   'user_notifications',
  globalAnnouncements: 'global_announcements',
  globalChats:         'global_chats',
  adminActionItems:    'admin_action_items',
  rankingCalculationRecords: 'ranking_calculation_records',
  volunteerApplications:     'volunteer_applications',
  jobPostings:               'job_postings',
  fundraisingDonations:      'fundraising_donations',
  blogPosts:                 'blog_posts',
  referralCampaigns:         'referral_campaigns',
  referralEvents:            'referral_events',
  userReferrals:             'user_referrals',
};

export { COUNTRIES_LIST, DEFAULT_COUNTRY } from './countries';

// ─── Admin Action Item Types & Status ──────────────────────────────────────────
export const ADMIN_ACTION_TYPES = {
  globalChat:           'global_chat',
  accountDeletion:      'account_deletion',
  volunteerApplication: 'volunteer_application',
  seasonReview:         'season_review',
  donationPledge:       'donation_pledge',
  referralMilestone:    'referral_milestone',
};

export const ACTION_ITEM_STATUS = {
  open:     'open',
  closed:   'closed',
  resolved: 'resolved',
};

export const APPLICATION_STATUS = {
  pending:     'pending',
  shortlisted: 'shortlisted',
  accepted:    'accepted',
  rejected:    'rejected',
};

// ─── Marketing Channels & Referral Constants ─────────────────────────────────
export const MARKETING_CHANNELS = [
  { value: 'instagram',     label: 'Instagram (Stories / Ads / Bio)',  icon: '📸' },
  { value: 'youtube',       label: 'YouTube (Video / Shorts)',         icon: '🎥' },
  { value: 'whatsapp',      label: 'WhatsApp Broadcast / Groups',      icon: '💬' },
  { value: 'church_flyer',  label: 'Church Partnership & Posters',     icon: '⛪' },
  { value: 'facebook',      label: 'Facebook / Meta Ads',              icon: '👥' },
  { value: 'school_youth',  label: 'School / Youth Fellowship Camp',   icon: '🎓' },
  { value: 'influencer',    label: 'Christian Influencer / Creator',   icon: '⭐' },
  { value: 'newsletter',    label: 'Email Newsletter',                 icon: '📧' },
  { value: 'other',         label: 'Other Marketing Channel',          icon: '🌐' },
];

export const CAMPAIGN_STATUS = {
  active:   'active',
  paused:   'paused',
  archived: 'archived',
};

// ─── User Roles ───────────────────────────────────────────────────────────────
export const ROLES = {
  superAdmin: 'Super Admin',
  admin:      'Admin',
  manager:    'Manager',
  staff:      'Staff',
  user:       'User',
};

/**
 * Normalizes any role string variation ('admin', 'ADMIN', 'superadmin', 'super_admin', etc.)
 * to standard canonical ROLES values.
 */
export const normalizeRole = (roleStr) => {
  if (!roleStr) return ROLES.user;
  const str = String(roleStr).trim().toLowerCase().replace(/[-_]/g, ' ');
  if (str === 'super admin' || str === 'superadmin' || str === 'super_admin' || str === 'owner') {
    return ROLES.superAdmin;
  }
  if (str === 'admin' || str === 'administrator') {
    return ROLES.admin;
  }
  if (str === 'manager') {
    return ROLES.manager;
  }
  if (str === 'staff' || str === 'support') {
    return ROLES.staff;
  }
  return ROLES.user;
};

// ─── Portal Modules ────────────────────────────────────────────────────────────
export const MODULES = {
  overview:      'overview',
  search:        'search',
  seasons:       'seasons',
  blogs:         'blogs',
  alerts:        'alerts',
  referrals:     'referrals',
  team:          'team',
  announcements: 'announcements',
  tickets:       'tickets',
  actions:       'actions',
};



export const PERMISSION_LEVELS = {
  none:  'none',
  read:  'read',
  write: 'write',
};

export const DEFAULT_ROLE_PERMISSIONS = {
  [ROLES.superAdmin]: {
    [MODULES.overview]:      PERMISSION_LEVELS.write,
    [MODULES.search]:        PERMISSION_LEVELS.write,
    [MODULES.seasons]:       PERMISSION_LEVELS.write,
    [MODULES.blogs]:         PERMISSION_LEVELS.write,
    [MODULES.alerts]:        PERMISSION_LEVELS.write,
    [MODULES.referrals]:     PERMISSION_LEVELS.write,
    [MODULES.team]:          PERMISSION_LEVELS.write,
    [MODULES.announcements]: PERMISSION_LEVELS.write,
    [MODULES.tickets]:       PERMISSION_LEVELS.write,
    [MODULES.actions]:       PERMISSION_LEVELS.write,
  },
  [ROLES.admin]: {
    [MODULES.overview]:      PERMISSION_LEVELS.write,
    [MODULES.search]:        PERMISSION_LEVELS.write,
    [MODULES.seasons]:       PERMISSION_LEVELS.write,
    [MODULES.blogs]:         PERMISSION_LEVELS.write,
    [MODULES.alerts]:        PERMISSION_LEVELS.write,
    [MODULES.referrals]:     PERMISSION_LEVELS.write,
    [MODULES.team]:          PERMISSION_LEVELS.write,
    [MODULES.announcements]: PERMISSION_LEVELS.write,
    [MODULES.tickets]:       PERMISSION_LEVELS.write,
    [MODULES.actions]:       PERMISSION_LEVELS.write,
  },
  [ROLES.manager]: {
    [MODULES.overview]:      PERMISSION_LEVELS.read,
    [MODULES.search]:        PERMISSION_LEVELS.read,
    [MODULES.seasons]:       PERMISSION_LEVELS.write,
    [MODULES.blogs]:         PERMISSION_LEVELS.write,
    [MODULES.alerts]:        PERMISSION_LEVELS.write,
    [MODULES.referrals]:     PERMISSION_LEVELS.write,
    [MODULES.team]:          PERMISSION_LEVELS.none,
    [MODULES.announcements]: PERMISSION_LEVELS.write,
    [MODULES.tickets]:       PERMISSION_LEVELS.write,
    [MODULES.actions]:       PERMISSION_LEVELS.read,
  },
  [ROLES.staff]: {
    [MODULES.overview]:      PERMISSION_LEVELS.read,
    [MODULES.search]:        PERMISSION_LEVELS.read,
    [MODULES.seasons]:       PERMISSION_LEVELS.read,
    [MODULES.blogs]:         PERMISSION_LEVELS.read,
    [MODULES.alerts]:        PERMISSION_LEVELS.write,
    [MODULES.referrals]:     PERMISSION_LEVELS.read,
    [MODULES.team]:          PERMISSION_LEVELS.none,
    [MODULES.announcements]: PERMISSION_LEVELS.read,
    [MODULES.tickets]:       PERMISSION_LEVELS.write,
    [MODULES.actions]:       PERMISSION_LEVELS.none,
  },
  [ROLES.user]: {
    [MODULES.overview]:      PERMISSION_LEVELS.read,  // can view overview & analytics
    [MODULES.search]:        PERMISSION_LEVELS.none,
    [MODULES.seasons]:       PERMISSION_LEVELS.write, // can manage own seasons
    [MODULES.blogs]:         PERMISSION_LEVELS.none,
    [MODULES.alerts]:        PERMISSION_LEVELS.none,
    [MODULES.referrals]:     PERMISSION_LEVELS.none,
    [MODULES.team]:          PERMISSION_LEVELS.none,
    [MODULES.announcements]: PERMISSION_LEVELS.none,
    [MODULES.tickets]:       PERMISSION_LEVELS.none,
    [MODULES.actions]:       PERMISSION_LEVELS.none,
  },
};

// ─── Quiz Status Options ───────────────────────────────────────────────────────
export const QUIZ_STATUS = {
  upcoming: 'Upcoming',
  ongoing:  'Ongoing',
  finished: 'Finished',
};

export const QUIZ_STATUS_OPTIONS = Object.values(QUIZ_STATUS).map((v) => ({
  value: v,
  label: v,
}));

// ─── Season Status Options ─────────────────────────────────────────────────────
export const SEASON_STATUS = {
  underReview: 'under-review',
  approved:    'approved',
  rejected:    'rejected',
  active:      'active',
  completed:   'completed',
  suspended:   'suspended',
  deactivated: 'deactivated',
};

export const SEASON_STATUS_CONFIG = {
  [SEASON_STATUS.underReview]: { label: 'Under Review', status: 'warning', icon: '⏳', desc: 'Pending admin review & host onboarding' },
  [SEASON_STATUS.approved]:    { label: 'Approved (Setup)', status: 'info', icon: '⚙️', desc: 'Approved. Host configuring quizzes & settings' },
  [SEASON_STATUS.rejected]:    { label: 'Rejected', status: 'danger', icon: '❌', desc: 'Season proposal rejected by admin team' },
  [SEASON_STATUS.active]:      { label: 'Live / Active', status: 'success', icon: '🟢', desc: 'Live on mobile app for contestants' },
  [SEASON_STATUS.completed]:   { label: 'Completed', status: 'neutral', icon: '🏁', desc: 'Contest completed and archived' },
  [SEASON_STATUS.suspended]:   { label: 'Suspended', status: 'danger', icon: '🚫', desc: 'Suspended for policy violation' },
  [SEASON_STATUS.deactivated]: { label: 'Deactivated', status: 'danger', icon: '🗑️', desc: 'Deactivated / Soft deleted' },
};

export const SEASON_STATUS_OPTIONS = [
  { value: 'active',       label: 'Live / Active' },
  { value: 'approved',     label: 'Approved (Workspace Setup)' },
  { value: 'completed',    label: 'Completed' },
  { value: 'under-review', label: 'Under Review' },
  { value: 'suspended',    label: 'Suspended' },
  { value: 'rejected',     label: 'Rejected' },
];


// ─── Allowed Playing Devices / Platforms ──────────────────────────────────────
export const ALLOWED_DEVICES = [
  {
    id: 'android',
    label: 'Android Mobile App',
    shortLabel: 'Android',
    icon: '🤖',
    description: 'Available on Google Play Store',
    disabled: false,
  },
  {
    id: 'web',
    label: 'Web Browser',
    shortLabel: 'Web',
    icon: '🌐',
    description: 'Play on Desktop & Mobile Web browsers',
    disabled: false,
  },
  {
    id: 'ios',
    label: 'iOS App',
    shortLabel: 'iOS',
    icon: '🍎',
    description: 'Apple App Store (Under Development)',
    disabled: true,
    badge: 'Coming Soon',
  },
];

// ─── Pagination ────────────────────────────────────────────────────────────────
export const ITEMS_PER_PAGE = 10;

// ─── Web Application & Deep Linking URLs ─────────────────────────────────────
export const SITE_BASE_URL = 'https://biblecontest.app';
export const ADMIN_WORKSPACE_URL = 'https://workspace.biblecontest.app';
export const DEEP_LINK_BASE_URL = 'https://go.biblecontest.app';
export const APP_BASE_URL = 'https://go.biblecontest.app'; // Routing, referrals & deep links
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.wtonline.biblecontest&hl=en_IN';

// ─── Volunteer & Full-Time Career Types ─────────────────────────────────────────
export const CAREER_TYPES = {
  volunteer: 'volunteer',
  fullTime:  'full_time',
};

// ─── Official Ministry Email Contacts ──────────────────────────────────────────
export const OFFICIAL_EMAILS = {
  support:        'support@biblecontest.app',
  manager:        'manager@biblecontest.app',
  ceo:            'ceo@biblecontest.app',
  jayaprakash:    'jayaprakashkumarnalla@biblecontest.app',
  manoharprakash: 'manoharprakashnalla@biblecontest.app',
  superAdmins: [
    'njpkumar9@gmail.com',
  ]
};

export * from './seasonConstants';



